import '../../scss/ExamDetail.scss';
import { Button } from 'primereact/button';
import React, { useState, useRef, useEffect, useCallback } from "react";
import { ThiTrucTuyenService } from 'services/ThiTrucTuyenService';
import { ProgressSpinner } from 'primereact/progressspinner';
import { useParams, useNavigate } from 'react-router-dom';
import { Toast } from 'primereact/toast';

const CEFR_THRESHOLDS = {
    NGUPHAP: { A0_min: 8, A1: 15, A2: 23, B1: 33, B2: 41 },
    NGHE: { A0_min: 8, A1: 15, A2: 23, B1: 33, B2: 41 },
    DOC: { A0_min: 8, A1: 15, A2: 25, B1: 37, B2: 45 },
    VIET: { A0_min: 6, A1: 17, A2: 25, B1: 39, B2: 47 },
    NOI: { A0_min: 4, A1: 15, A2: 25, B1: 40, B2: 47 },
};

function getCEFR(skill, score) {
    score = parseFloat(score || 0);
    const t = CEFR_THRESHOLDS[skill];
    if (!t) return '-';
    if (score < t.A0_min) return 'A0';
    if (score <= t.A1) return 'A1';
    if (score <= t.A2) return 'A2';
    if (score <= t.B1) return 'B1';
    if (score <= t.B2) return 'B2';
    return 'C1';
}

function cefrToNum(cefr) {
    const map = { A1: 1, A2: 2, B1: 3, B2: 4, C1: 5 };
    return map[cefr] !== undefined ? map[cefr] : 0;
}

function numToCEFR(num) {
    const map = { 1: 'A1', 2: 'A2', 3: 'B1', 4: 'B2', 5: 'C1' };
    return map[num] || '-';
}

const CEFR_COLORS = {
    A0: { bg: '#f3f4f6', color: '#6b7280', border: '#d1d5db' },
    A1: { bg: '#e0f2fe', color: '#0369a1', border: '#7dd3fc' },
    A2: { bg: '#dbeafe', color: '#1d4ed8', border: '#93c5fd' },
    B1: { bg: '#dcfce7', color: '#15803d', border: '#86efac' },
    B2: { bg: '#fef3c7', color: '#a16207', border: '#fcd34d' },
    C1: { bg: '#fee2e2', color: '#b91c1c', border: '#fca5a5' },
};

const SKILL_META = {
    NGUPHAP: { label: 'Ngữ pháp & Từ vựng', icon: '📝', englabel: 'Grammar & Vocabulary' },
    NGHE: { label: 'Nghe', icon: '🎧', englabel: 'Listening' },
    DOC: { label: 'Đọc', icon: '📖', englabel: 'Reading' },
    NOI: { label: 'Nói', icon: '🎙️', englabel: 'Speaking' },
    VIET: { label: 'Viết', icon: '✍️', englabel: 'Writing' },
};

const AI_SKILLS = ['NOI', 'VIET'];

export default function PublicExamResult() {
    const { userId, dethiId } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [scores, setScores] = useState([]);
    const [error, setError] = useState(null);
    const [attemptCode, setAttemptCode] = useState(null);
    const toast = useRef(null);

    const fetchScores = useCallback(async () => {
        try {
            setError(null);
            const result = await ThiTrucTuyenService.getPublicOverviewExam(userId, dethiId);
            const rawScores = result?.data?.scores || [];
            setScores(rawScores);

            if (rawScores.length) {
                const sortedScores = [...rawScores].sort((a, b) =>
                    new Date(b.time_bat_dau_thi || 0) - new Date(a.time_bat_dau_thi || 0)
                );
                const codeFromApi = sortedScores.find(s => s.attempt_code && s.type_exam !== 'NOI')?.attempt_code
                    || sortedScores.find(s => s.attempt_code)?.attempt_code
                    || null;

                setAttemptCode(codeFromApi);
            }

            return rawScores;
        } catch (err) {
            console.error('[PublicExamResult] fetchScores error:', err);
            setError(err.response?.data?.message || err.response?.data?.error || "Không thể tải dữ liệu kết quả thi");
            return [];
        }
    }, [userId, dethiId]);

    useEffect(() => {
        const init = async () => {
            setLoading(true);
            await fetchScores();
            setLoading(false);
        };
        init();
    }, [fetchScores]);

    const computeSkillData = () => {
        const skillOrder = ['NGUPHAP', 'NGHE', 'DOC', 'NOI', 'VIET'];
        const scoreMap = {};
        scores.forEach(row => { scoreMap[row.type_exam] = row; });

        const nguphapRow = scoreMap['NGUPHAP'];
        const nguphapScore = nguphapRow ? parseFloat(nguphapRow.tong_diem || 0) : 0;
        const hasBonus = nguphapScore >= 32;

        const applyBonus = (rawVal) => {
            if (rawVal === null) return null;
            const v = parseFloat(rawVal);
            return hasBonus ? Math.min(50, Math.round(v * 1.05)) : v;
        };

        const getScaleScore = (key) => {
            const row = scoreMap[key];
            if (!row) return null;
            const stored = row.quy_diem !== undefined ? parseFloat(row.quy_diem) : 0;
            const raw = row.tong_diem !== undefined ? parseFloat(row.tong_diem) : null;
            if (stored > 0) return stored;
            if (raw === null) return null;
            if (key === 'NGHE') return Math.min(50, raw * 2);
            if (key === 'DOC') return Math.min(50, raw / 25 * 50);
            return raw;
        };

        const displayScores = {
            NGUPHAP: nguphapRow ? parseFloat(nguphapRow.tong_diem || 0) : null,
            NGHE: applyBonus(getScaleScore('NGHE')),
            DOC: applyBonus(getScaleScore('DOC')),
            NOI: applyBonus(getScaleScore('NOI')),
            VIET: applyBonus(getScaleScore('VIET')),
        };

        const cefrResults = {};
        for (const key of skillOrder) {
            cefrResults[key] = displayScores[key] !== null ? getCEFR(key, displayScores[key]) : '-';
        }

        let tongDiem = 0;
        ['NGHE', 'DOC', 'NOI', 'VIET'].forEach(k => {
            if (displayScores[k] !== null) tongDiem += displayScores[k];
        });

        let cefrOverall = '-';
        const fourCefrs = ['NGHE', 'DOC', 'NOI', 'VIET'].map(k => cefrResults[k]);
        const hasA0 = fourCefrs.some(c => c === 'A0');
        const allDone = fourCefrs.every(c => c !== '-');
        if (allDone && !hasA0) {
            const nums = fourCefrs.map(cefrToNum);
            const avg = nums.reduce((a, b) => a + b, 0) / 4;
            cefrOverall = numToCEFR(Math.round(avg));
        }

        const skills = skillOrder.map(key => {
            const row = scoreMap[key];
            const isAiSkill = AI_SKILLS.includes(key);
            const aiGraded = row ? parseInt(row.ai_graded || 0) : 0;
            const waitingAi = isAiSkill && row && aiGraded === 0;

            return {
                key,
                meta: SKILL_META[key],
                displayScore: displayScores[key],
                rawScore: row ? row.tong_diem : null,
                cefr: cefrResults[key],
                hasTaken: !!row,
                hasBonus: key !== 'NGUPHAP' && hasBonus && displayScores[key] !== null,
                waitingAi,
                aiGraded: aiGraded === 1,
            };
        });

        return { skills, cefrOverall, tongDiem, hasBonus };
    };

    const computed = scores.length > 0 ? computeSkillData() : { skills: [], cefrOverall: '-', tongDiem: 0, hasBonus: false };
    const { skills, cefrOverall, tongDiem, hasBonus } = computed;

    const CefrBadge = ({ level, size = 'normal' }) => {
        if (!level || level === '-') return null;
        const colors = CEFR_COLORS[level] || CEFR_COLORS.A0;
        const isSmall = size === 'small';
        return (
            <span style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                background: colors.bg, color: colors.color, border: `2px solid ${colors.border}`,
                borderRadius: isSmall ? 6 : 10,
                padding: isSmall ? '2px 10px' : '6px 18px',
                fontWeight: 800, fontSize: isSmall ? 13 : 18,
                letterSpacing: 1.5, minWidth: isSmall ? 42 : 56,
                fontFamily: "'Segoe UI', 'Roboto', monospace",
            }}>
                {level}
            </span>
        );
    };

    const SkillCard = ({ skill }) => {
        const { meta, displayScore, cefr, hasTaken, waitingAi, hasBonus, key } = skill;

        // Ưu tiên lấy bản ghi is_completed=1 khi có nhiều bản ghi cùng type_exam
        const row = scores.filter(s => s.type_exam === key).find(s => parseInt(s.is_completed) === 1)
            || scores.find(s => s.type_exam === key);

        // Lấy trực tiếp từ DB, không tự tính lại
        const tongDiem = row ? parseFloat(row.tong_diem || 0) : 0;   // điểm thô
        const quyDiem = row ? parseFloat(row.quy_diem || 0) : 0;   // điểm quy đổi
        const raw = tongDiem; // giữ tên raw để dùng trong NGUPHAP
        const thresholds = CEFR_THRESHOLDS[key];

        // Hàm helper vẽ Step
        const Step = ({ label, value, sub, isLast = false, color = '#6366f1' }) => (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 13, fontWeight: 800, color: color }}>{value}</div>
                    <div style={{ fontSize: 9, color: '#9ca3af', textTransform: 'uppercase', fontWeight: 600 }}>{label}</div>
                    {sub && <div style={{ fontSize: 8, color: '#6366f1', transform: 'scale(0.9)' }}>{sub}</div>}
                </div>
                {!isLast && <i className="pi pi-angle-right" style={{ fontSize: 10, color: '#d1d5db', marginTop: -8 }}></i>}
            </div>
        );

        let steps = [];
        if (hasTaken && !waitingAi) {
            if (key === 'NGUPHAP') {
                steps.push(<Step key="raw" label="Điểm thô" value={`${tongDiem}/50`} />);
                steps.push(<Step key="final" label="Kết quả" value={tongDiem} isLast={true} color="#1e1b4b" />);
            } else if (key === 'NGHE' || key === 'DOC') {
                // NGHE/DOC: hiển thị Điểm thô → Quy đổi
                steps.push(<Step key="raw" label="Điểm thô" value={tongDiem} />);
                steps.push(<Step key="scaled" label="Quy đổi" value={quyDiem} />);
                if (hasBonus) {
                    const bonusVal = quyDiem * 1.05;
                    steps.push(<Step key="bonus" label="Thưởng x1.05" value={bonusVal.toFixed(2)} />);
                    steps.push(<Step key="rounded" label="Làm tròn" value={Math.round(bonusVal)} color="#1e1b4b" />);
                }
                steps.push(<Step key="grade" label="Xếp loại" value={cefr} isLast={true} color="#6366f1" />);
            } else {
                // NOI/VIET: không hiển thị điểm thô, chỉ hiện Quy đổi
                steps.push(<Step key="scaled" label="Quy đổi" value={quyDiem} />);
                if (hasBonus) {
                    const bonusVal = quyDiem * 1.05;
                    steps.push(<Step key="bonus" label="Thưởng x1.05" value={bonusVal.toFixed(2)} />);
                    steps.push(<Step key="rounded" label="Làm tròn" value={Math.round(bonusVal)} color="#1e1b4b" />);
                }
                steps.push(<Step key="grade" label="Xếp loại" value={cefr} isLast={true} color="#6366f1" />);
            }
        }

        return (
            <div style={{
                background: '#fff',
                borderRadius: 20,
                padding: '24px',
                boxShadow: '0 4px 15px rgba(0,0,0,0.03)',
                border: '1px solid #f1f5f9',
                display: 'flex', flexDirection: 'column', gap: 16,
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                    <div style={{
                        width: 56, height: 56, borderRadius: 16,
                        background: 'linear-gradient(135deg, #6366f11a, #a855f71a)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 28, flexShrink: 0,
                    }}>
                        {meta.icon}
                    </div>
                    <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 800, fontSize: 17, color: '#1e293b' }}>{meta.label}</div>
                        {/* {key !== 'NGUPHAP' && (
                            <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600, display: 'flex', gap: 8, marginTop: 4 }}>
                                {thresholds && Object.entries(thresholds).filter(([k]) => k !== 'A0_min').map(([lvl, val]) => (
                                    <span key={lvl} style={{ color: cefr === lvl ? '#6366f1' : '#cbd5e1', borderBottom: cefr === lvl ? '2px solid' : 'none' }}>
                                        {lvl}:{val}
                                    </span>
                                ))}
                            </div>
                        )} */}
                    </div>
                    <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: 28, fontWeight: 900, color: '#0f172a', fontFamily: "'Segoe UI', 'Roboto', sans-serif" }}>
                            {hasTaken && !waitingAi ? parseFloat(displayScore).toFixed(1) : (waitingAi ? '...' : '–')}
                        </div>
                        <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 700, letterSpacing: 0.5 }}>
                            {key === 'NGUPHAP' ? 'ĐIỂM THỰC TẾ' : 'ĐIỂM QUY ĐỔI'}
                        </div>
                    </div>
                </div>

                {hasTaken && !waitingAi && (
                    <div style={{
                        background: '#f8fafc',
                        padding: '12px 20px',
                        borderRadius: 14,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        border: '1px solid #edf2f7'
                    }}>
                        <div style={{ display: 'flex', gap: 12 }}>
                            {steps}
                        </div>
                        <div style={{ flexShrink: 0, textAlign: 'center' }}>
                            {key !== 'NGUPHAP' && <CefrBadge level={cefr} />}
                            {key !== 'NGUPHAP' && <div style={{ fontSize: 9, color: '#94a3b8', fontWeight: 700, marginTop: 4, letterSpacing: 0.5 }}>ĐIỂM CEFR</div>}
                        </div>
                    </div>
                )}


            </div>
        );
    };

    if (loading) return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: '#f8fafc' }}><ProgressSpinner /></div>;

    const nguphapScore = scores.find(s => s.type_exam === 'NGUPHAP')?.tong_diem || 0;

    return (
        <div className="result-container" style={{ background: '#f8fafc', minHeight: '100vh', padding: '60px 20px', fontFamily: "'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif" }}>
            <Toast ref={toast} />
            <div className="result-wrapper" style={{ maxWidth: 720, margin: '0 auto' }}>
                {error && (
                    <div style={{ padding: 20, background: '#fef2f2', color: '#dc2626', borderRadius: 20, textAlign: 'center', marginBottom: 24, border: '1px solid #fee2e2', fontWeight: 700, boxShadow: '0 4px 12px rgba(220, 38, 38, 0.05)' }}>
                        ⚠️ {error}
                    </div>
                )}

                <div className="congrats-banner" style={{ background: '#fff', borderRadius: 24, padding: '48px 40px', textAlign: 'center', boxShadow: '0 10px 30px rgba(0,0,0,0.04)', border: '1px solid #f1f5f9', position: 'relative', overflow: 'hidden' }}>
                    <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: 6, background: 'linear-gradient(90deg, #6366f1, #a855f7)' }}></div>

                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 30, marginBottom: 30 }}>
                        <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: 12, color: '#64748b', fontWeight: 700, textTransform: 'uppercase', marginBottom: 8 }}>Trình độ</div>
                            <CefrBadge level={cefrOverall} />
                        </div>
                        <div style={{ width: 1, height: 60, background: '#f1f5f9' }}></div>
                        <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: 12, color: '#64748b', fontWeight: 700, textTransform: 'uppercase', marginBottom: 4 }}>Tổng điểm 4 kỹ năng</div>
                            <div style={{ fontSize: 42, fontWeight: 900, color: '#0f172a' }}>{tongDiem.toFixed(1)}<span style={{ fontSize: 18, color: '#94a3b8', fontWeight: 600 }}>/200</span></div>
                        </div>
                    </div>

                    <h1 style={{ fontSize: 26, fontWeight: 900, color: '#1e293b', marginBottom: 8 }}>BÁO CÁO KẾT QUẢ THI</h1>
                    <p style={{ color: '#64748b' }}>User ID: <b>{userId}</b></p>

                    {attemptCode && (
                        <div style={{ marginTop: 24 }}>
                            <Button
                                label="XEM CHI TIẾT BÀI LÀM"
                                icon="pi pi-search"
                                className="p-button-raised p-button-indigo"
                                onClick={() => navigate(`/public-review/${userId}/${attemptCode}`)}
                                style={{ borderRadius: 16, padding: '14px 28px', fontWeight: 800, letterSpacing: 0.5 }}
                            />
                        </div>
                    )}
                </div>

                <div style={{ marginTop: 30 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, padding: '0 10px' }}>
                        <h3 style={{ fontSize: 18, fontWeight: 800, color: '#1e293b', margin: 0 }}>📊 PHÂN TÍCH ĐIỂM SỐ</h3>
                        {hasBonus && (
                            <div style={{ background: '#f0fdf4', color: '#16a34a', fontSize: 12, padding: '6px 14px', borderRadius: 24, fontWeight: 800, border: '1px solid #bbf7d0', boxShadow: '0 2px 8px rgba(22, 163, 74, 0.05)' }}>
                                BONUS 1.05 (GRAMMAR ≥ 32)
                            </div>
                        )}
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                        {skills.map(skill => <SkillCard key={skill.key} skill={skill} />)}
                    </div>
                </div>

                <div style={{ background: 'linear-gradient(135deg, #eff6ff 0%, #f5f3ff 100%)', borderRadius: 24, padding: 30, marginTop: 24, border: '1px solid #e0e7ff', boxShadow: '0 8px 20px rgba(99, 102, 241, 0.05)' }}>
                    <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 15, color: '#1e1b4b', display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ width: 32, height: 32, background: '#fff', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <i className="pi pi-calculator" style={{ color: '#6366f1' }}></i>
                        </div>
                        TÍNH ĐIỂM OVERALL
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, fontSize: 14, color: '#475569', fontWeight: 500 }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                            <div style={{ width: 20, height: 20, background: '#6366f1', color: '#fff', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 800, flexShrink: 0, marginTop: 2 }}>1</div>
                            <div><b>Kiểm tra điều kiện:</b> Ngữ pháp đạt <b>{nguphapScore}/50</b>. {hasBonus ? 'Thỏa mãn ≥ 32, x1.05 cho 4 kỹ năng.' : ''}</div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                            <div style={{ width: 20, height: 20, background: '#6366f1', color: '#fff', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 800, flexShrink: 0, marginTop: 2 }}>2</div>
                            <div><b>Quy đổi CEFR:</b> ({skills.filter(s => s.key !== 'NGUPHAP').map(s => `${s.key}:${cefrToNum(s.cefr)}`).join(', ')})</div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                            <div style={{ width: 20, height: 20, background: '#6366f1', color: '#fff', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 800, flexShrink: 0, marginTop: 2 }}>3</div>
                            <div style={{ padding: '12px 18px', background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0', marginTop: 8 }}>
                                <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 4 }}>CÔNG THỨC TRUNG BÌNH CỘNG</div>
                                <div style={{ fontSize: 18, color: '#1e293b', fontWeight: 800 }}>
                                    ({skills.filter(s => s.key !== 'NGUPHAP').map(s => cefrToNum(s.cefr)).join(' + ')}) / 4 = <span style={{ color: '#6366f1' }}>{(skills.filter(s => s.key !== 'NGUPHAP').reduce((a, b) => a + cefrToNum(b.cefr), 0) / 4).toFixed(2)}</span>
                                    <i className="pi pi-arrow-right" style={{ margin: '0 12px', fontSize: 12, color: '#cbd5e1' }}></i>
                                    <span style={{ fontSize: 22, color: '#1e1b4b' }}>{cefrOverall}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div style={{ textAlign: 'center', marginTop: 32 }}>
                    <Button
                        label="QUAY LẠI TRANG CHỦ"
                        className="p-button-text"
                        onClick={() => navigate('/')}
                        style={{ color: '#94a3b8', fontWeight: 800, fontSize: 14 }}
                    />
                </div>
            </div>
        </div>
    );
}
